import { Box, Typography } from "@mui/material";
import React, { useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { GvPrimaryButton } from "../../../components";
import { IPetItemResponse } from "../../../services/pet-list/pet-list.interface";
import {
  ASSIGNED_BOOKING_TYPE, CREATE_BOOKING_TYPE, MODALS,
} from "../../../config/params";
import {
  useAppointmentStore,
} from "../../../hooks/redux-hooks/appointment-store/appointment.store";
import { useModal } from "../../../context/ModalContext";
import { useBookingAppointmentTypeStore } from "../../../hooks";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { userActivityLogger } from "../../../utils/mixpanel";
import PetSelection from "../../../components/PetSelection/PetSelection";

const getApptTypeLabel = (createType: number): string => {
  switch (createType) {
  case ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING:
    return "express_visit";
  case ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING:
    return "drop_off";
  case ASSIGNED_BOOKING_TYPE.URGENT_BOOKING:
    return "urgent_visit";
  default:
    return "in_room_visit";
  }
};

const GroupedAppointmentActionModal = () => {
  const {
    groupedAppointments, setSelectedVisit,
    setAppointmentDetails, setGroupedAppointments,
  } = useAppointmentStore();
  const { closeModal, openModal, params } = useModal();
  const {
    setBookingAppointmentType, setDefaultBookingType,
    setAppointmentTypeIndicator,
  } = useBookingAppointmentTypeStore();
  const action = (params?.action as string) || "cancel";
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  if (!groupedAppointments || groupedAppointments.length === 0) {
    return null;
  }

  const isUrgent = groupedAppointments.some(
    (a) => a.appointmentCreateType ===
      ASSIGNED_BOOKING_TYPE.URGENT_BOOKING,
  );

  const togglePet = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ?
        prev.filter((pid) => pid !== id) : [...prev, id],
    );
  };

  const petsFromAppointments: IPetItemResponse[] =
    groupedAppointments.map(
      (a) => ({ ...a.pet } as unknown as IPetItemResponse),
    );

  const selectedPetItems: IPetItemResponse[] =
    groupedAppointments
      .filter((a) => selectedIds.includes(a.id))
      .map(
        (a) => ({ ...a.pet } as unknown as IPetItemResponse),
      );

  const handleSelectPet = (pet: IPetItemResponse) => {
    const matchingAppt = groupedAppointments.find(
      (a) => a.pet.id === pet.id,
    );
    if (!matchingAppt) return;
    togglePet(matchingAppt.id);
  };

  const getTitle = () => {
    if (action === "cancel" && isUrgent) {
      return "For which of your pets you want to " +
        "cancel an urgent visit?";
    }
    if (action === "cancel") {
      return "Which of your pets do you want to " +
        "cancel a visit for?";
    }
    return "Which of your pets do you want to " +
      "reschedule a visit for?";
  };

  const getHeaderText = () => {
    if (action === "cancel" && isUrgent) {
      return "Cancel Urgent Visit";
    }
    return action === "cancel" ?
      "Cancel Visit" : "Reschedule Visit";
  };

  const handleConfirm = () => {
    const selectedAppointments = groupedAppointments.filter(
      (a) => selectedIds.includes(a.id),
    );
    if (selectedAppointments.length === 0) return;

    const firstSelected = selectedAppointments[0];
    setSelectedVisit(firstSelected);
    setAppointmentDetails(firstSelected);
    setAppointmentTypeIndicator(
      firstSelected.appointmentCreateType,
    );

    if (action === "reschedule") {
      userActivityLogger(MIXPANEL_EVENTS.RESCHEDULE_VISIT, {
        referred_from: "Dashboard",
        pet_name: firstSelected.pet.name,
        appt_type: getApptTypeLabel(
          firstSelected.appointmentCreateType),
      });

      // Note: urgent reschedule is handled directly by
      // GroupedAppointmentCard and AppointmentReviewContainer
      // before this modal is opened, so no urgent branch here.
      if (
        firstSelected.appointmentCreateType ===
        ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING
      ) {
        setBookingAppointmentType(
          CREATE_BOOKING_TYPE.DROPOFF_BOOKING);
        openModal(MODALS.RESCHEDULE_A_VISIT);
      } else if (
        firstSelected.appointmentCreateType ===
        ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING
      ) {
        setDefaultBookingType();
        openModal(MODALS.RESCHEDULE_EXPRESS_VISIT_MODAL);
      } else {
        setDefaultBookingType();
        openModal(MODALS.RESCHEDULE_A_VISIT);
      }
    } else {
      setGroupedAppointments(selectedAppointments);
      openModal(MODALS.VISIT_CANCEL);
    }
  };

  return (
    <Box className="flex flex-col h-full">
      <Box className="shrink-0 p-1">
        <Box className="flex justify-between w-full">
          <span
            className="cursor-pointer"
            onClick={() => openModal(MODALS.APPOINTMENT_REVIEW)}
          >
            <ArrowBackIcon />
          </span>
          <span
            className="cursor-pointer"
            onClick={closeModal}
          >
            <CloseIcon />
          </span>
        </Box>
        <Typography
          className="!mb-4 text-center !text-2xl
            !font-semibold !grenette-pro"
        >
          {getHeaderText()}
        </Typography>
      </Box>

      <Box className="flex flex-col grow px-4
        overflow-y-auto min-h-0"
      >
        <PetSelection
          petsActive={petsFromAppointments}
          selectedPets={selectedPetItems}
          title={getTitle()}
          handleSelectPet={handleSelectPet}
          handleClickAddAPet={() => {}}
          hideAddPet
        />
      </Box>

      <Box className="shrink-0 py-6 px-8">
        <GvPrimaryButton
          type="button"
          isDisabled={selectedIds.length === 0}
          onClick={handleConfirm}
        >
          Continue
        </GvPrimaryButton>
      </Box>
    </Box>
  );
};

export default GroupedAppointmentActionModal;
